import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Target,
  Scale,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  FileText,
  StickyNote,
  Plus,
  Edit,
  Trash2,
  Dumbbell,
  Heart,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Save,
  Loader2,
  Camera,
  Upload,
  Image,
  X,
  ZoomIn,
  ArrowLeftRight,
  Sparkles,
  FileBarChart,
  Copy,
  RefreshCw,
  Share2,
  Send,
  CheckCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

export default function ClientProfilePage() {
  const { clientId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [chartData, setChartData] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddBodyComp, setShowAddBodyComp] = useState(false);
  const [newNote, setNewNote] = useState({ note: '', category: 'general' });
  const [newBodyComp, setNewBodyComp] = useState({});
  const [saving, setSaving] = useState(false);
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [showPhotoModal, setShowPhotoModal] = useState(null);
  const [showCompareMode, setShowCompareMode] = useState(false);
  const [comparePhotos, setComparePhotos] = useState({ before: null, after: null });
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summaryType, setSummaryType] = useState('weekly');
  const [customNote, setCustomNote] = useState('');
  const [latestSummary, setLatestSummary] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailServiceStatus, setEmailServiceStatus] = useState(null);
  const photoInputRefs = {
    front: useRef(null),
    side: useRef(null),
    back: useRef(null)
  };

  useEffect(() => {
    fetchProfile();
    fetchChartData();
    fetchProgressPhotos();
    fetchSummaries();
  }, [clientId, token]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/clients/profile/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load client profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await axios.get(`${API}/clients/progress/${clientId}/chart-data`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { metric: 'weight', days: 90 }
      });
      setChartData(response.data.chart_data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchProgressPhotos = async () => {
    try {
      const response = await axios.get(`${API}/clients/progress-photos/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgressPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error fetching progress photos:', error);
    }
  };

  const handlePhotoUpload = async (entryId, photoType, file) => {
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploadingPhoto(`${entryId}-${photoType}`);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        try {
          await axios.post(`${API}/clients/body-composition/photo`, {
            entry_id: entryId,
            photo_type: photoType,
            photo_data: base64Data
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success(`${photoType.charAt(0).toUpperCase() + photoType.slice(1)} photo uploaded!`);
          fetchProfile();
          fetchProgressPhotos();
        } catch (error) {
          toast.error('Failed to upload photo');
        } finally {
          setUploadingPhoto(null);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Error processing photo');
      setUploadingPhoto(null);
    }
  };

  const handleDeletePhoto = async (entryId, photoType) => {
    if (!window.confirm(`Delete ${photoType} photo?`)) return;
    
    try {
      await axios.delete(`${API}/clients/body-composition/${entryId}/photo/${photoType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      fetchProfile();
      fetchProgressPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const fetchSummaries = async () => {
    try {
      const response = await axios.get(`${API}/clients/progress-summaries/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummaries(response.data.summaries || []);
    } catch (error) {
      console.error('Error fetching summaries:', error);
    }
  };

  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const response = await axios.post(`${API}/clients/progress-summary/${clientId}`, {
        summary_type: summaryType,
        include_photos: false,
        custom_note: customNote || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLatestSummary(response.data);
      fetchSummaries();
      toast.success('Progress summary generated!');
      setCustomNote('');
    } catch (error) {
      console.error('Summary error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const deleteSummary = async (summaryId) => {
    if (!window.confirm('Delete this summary?')) return;
    
    try {
      await axios.delete(`${API}/clients/progress-summaries/${summaryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Summary deleted');
      fetchSummaries();
      if (latestSummary?.summary_id === summaryId) {
        setLatestSummary(null);
      }
    } catch (error) {
      toast.error('Failed to delete summary');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const checkEmailServiceStatus = async () => {
    try {
      const response = await axios.get(`${API}/clients/email-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailServiceStatus(response.data);
    } catch (error) {
      console.error('Error checking email status:', error);
    }
  };

  const sendProgressEmail = async (summaryId = null) => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/clients/send-progress-email/${clientId}`, {
        summary_id: summaryId,
        summary_type: summaryType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || 'Email sent successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send email';
      toast.error(message);
    } finally {
      setSendingEmail(false);
    }
  };

  // Fetch email status on mount
  useEffect(() => {
    checkEmailServiceStatus();
  }, [token]);

  const handleAddNote = async () => {
    if (!newNote.note.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/clients/notes/${clientId}`, newNote, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note added');
      setNewNote({ note: '', category: 'general' });
      setShowAddNote(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      await axios.delete(`${API}/clients/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note deleted');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleAddBodyComp = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/clients/body-composition/${clientId}`, newBodyComp, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Body composition entry added');
      setNewBodyComp({});
      setShowAddBodyComp(false);
      fetchProfile();
      fetchChartData();
    } catch (error) {
      toast.error('Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-gray-500/20 text-gray-400',
      workout: 'bg-blue-500/20 text-blue-400',
      nutrition: 'bg-green-500/20 text-green-400',
      progress: 'bg-purple-500/20 text-purple-400',
      concern: 'bg-red-500/20 text-red-400',
      goal: 'bg-yellow-500/20 text-yellow-400'
    };
    return colors[category] || colors.general;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl text-white">Client Not Found</h2>
        <Button onClick={() => navigate('/clients')} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  const { user: clientData, intake_form, body_compositions, notes, programs, progress } = profile;

  return (
    <div data-testid="client-profile-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/clients')}
          className="p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase">
            {clientData.first_name && clientData.last_name 
              ? `${clientData.first_name} ${clientData.last_name}`
              : clientData.email}
          </h1>
          <p className="text-gray-400 text-sm">{clientData.email}</p>
        </div>
        <Button
          onClick={() => navigate(`/clients/intake/${clientId}`)}
          variant="outline"
        >
          <Edit className="w-4 h-4 mr-2" />
          Edit Intake
        </Button>
      </div>

      {/* Quick Stats */}
      {progress && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-tactical">
            <div className="text-gray-400 text-xs mb-1">Starting Weight</div>
            <div className="text-white font-mono text-xl">{progress.starting_weight || '-'} lbs</div>
          </div>
          <div className="card-tactical">
            <div className="text-gray-400 text-xs mb-1">Current Weight</div>
            <div className="text-white font-mono text-xl">{progress.current_weight || '-'} lbs</div>
          </div>
          <div className="card-tactical">
            <div className="text-gray-400 text-xs mb-1">Weight Change</div>
            <div className={`font-mono text-xl flex items-center gap-1 ${
              progress.weight_change < 0 ? 'text-green-400' : progress.weight_change > 0 ? 'text-red-400' : 'text-gray-400'
            }`}>
              {progress.weight_change !== null ? (
                <>
                  {progress.weight_change < 0 ? <TrendingDown className="w-4 h-4" /> : 
                   progress.weight_change > 0 ? <TrendingUp className="w-4 h-4" /> : 
                   <Minus className="w-4 h-4" />}
                  {Math.abs(progress.weight_change)} lbs
                </>
              ) : '-'}
            </div>
          </div>
          <div className="card-tactical">
            <div className="text-gray-400 text-xs mb-1">Body Fat Change</div>
            <div className={`font-mono text-xl flex items-center gap-1 ${
              progress.body_fat_change < 0 ? 'text-green-400' : progress.body_fat_change > 0 ? 'text-red-400' : 'text-gray-400'
            }`}>
              {progress.body_fat_change !== null ? (
                <>
                  {progress.body_fat_change < 0 ? <TrendingDown className="w-4 h-4" /> : 
                   progress.body_fat_change > 0 ? <TrendingUp className="w-4 h-4" /> : 
                   <Minus className="w-4 h-4" />}
                  {Math.abs(progress.body_fat_change)}%
                </>
              ) : '-'}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
        {['overview', 'body-composition', 'summaries', 'notes', 'programs'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab 
                ? 'bg-primary/20 text-primary border-b-2 border-primary' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab === 'overview' && <User className="w-4 h-4 inline mr-2" />}
            {tab === 'body-composition' && <Scale className="w-4 h-4 inline mr-2" />}
            {tab === 'summaries' && <Sparkles className="w-4 h-4 inline mr-2" />}
            {tab === 'notes' && <StickyNote className="w-4 h-4 inline mr-2" />}
            {tab === 'programs' && <Dumbbell className="w-4 h-4 inline mr-2" />}
            {tab === 'summaries' ? 'AI Summaries' : tab.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Personal Info */}
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Personal Information
            </h3>
            {intake_form ? (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Full Name</span>
                  <span className="text-white">{intake_form.first_name} {intake_form.last_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Date of Birth</span>
                  <span className="text-white">{intake_form.date_of_birth}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Gender</span>
                  <span className="text-white capitalize">{intake_form.gender}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Phone</span>
                  <span className="text-white">{intake_form.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Occupation</span>
                  <span className="text-white">{intake_form.occupation || '-'}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No intake form submitted yet.</p>
            )}
          </div>

          {/* Goals */}
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Goals
            </h3>
            {intake_form?.goals ? (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Primary Goal</span>
                  <span className="text-primary font-semibold capitalize">
                    {intake_form.goals.primary_goal?.replace(/_/g, ' ')}
                  </span>
                </div>
                {intake_form.goals.target_weight && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Target Weight</span>
                    <span className="text-white">{intake_form.goals.target_weight} lbs</span>
                  </div>
                )}
                {intake_form.goals.target_body_fat && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Target Body Fat</span>
                    <span className="text-white">{intake_form.goals.target_body_fat}%</span>
                  </div>
                )}
                {intake_form.goals.motivation && (
                  <div className="mt-3 p-3 bg-secondary/50 rounded">
                    <div className="text-gray-400 text-xs mb-1">Motivation</div>
                    <div className="text-white">{intake_form.goals.motivation}</div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No goals set yet.</p>
            )}
          </div>

          {/* Medical Info */}
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              Medical Information
            </h3>
            {intake_form?.medical_info ? (
              <div className="space-y-3 text-sm">
                {intake_form.medical_info.conditions?.length > 0 && (
                  <div>
                    <span className="text-gray-400">Conditions:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {intake_form.medical_info.conditions.map((c, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {intake_form.medical_info.injuries?.length > 0 && (
                  <div>
                    <span className="text-gray-400">Injuries:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {intake_form.medical_info.injuries.map((i, idx) => (
                        <span key={idx} className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                          {i}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Physician Clearance</span>
                  <span className={intake_form.medical_info.physician_clearance ? 'text-green-400' : 'text-yellow-400'}>
                    {intake_form.medical_info.physician_clearance ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No medical information provided.</p>
            )}
          </div>

          {/* Emergency Contact */}
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-primary" />
              Emergency Contact
            </h3>
            {intake_form?.emergency_contact ? (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Name</span>
                  <span className="text-white">{intake_form.emergency_contact.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Relationship</span>
                  <span className="text-white">{intake_form.emergency_contact.relationship}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Phone</span>
                  <span className="text-white">{intake_form.emergency_contact.phone}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No emergency contact provided.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'body-composition' && (
        <div className="space-y-6">
          {/* Add Entry Button */}
          <div className="flex justify-end">
            <Button onClick={() => setShowAddBodyComp(!showAddBodyComp)} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>

          {/* Add Entry Form */}
          {showAddBodyComp && (
            <div className="card-tactical">
              <h3 className="font-heading text-lg text-white mb-4">New Body Composition Entry</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-gray-400 text-xs">Date</label>
                  <Input
                    type="date"
                    value={newBodyComp.date || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, date: e.target.value})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Weight (lbs)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newBodyComp.weight_lbs || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, weight_lbs: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Body Fat %</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newBodyComp.body_fat_percentage || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, body_fat_percentage: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Chest (in)</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={newBodyComp.chest || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, chest: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Waist (in)</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={newBodyComp.waist || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, waist: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Hips (in)</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={newBodyComp.hips || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, hips: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Left Arm (in)</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={newBodyComp.left_arm || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, left_arm: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Right Arm (in)</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={newBodyComp.right_arm || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, right_arm: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Left Thigh (in)</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={newBodyComp.left_thigh || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, left_thigh: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Right Thigh (in)</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={newBodyComp.right_thigh || ''}
                    onChange={(e) => setNewBodyComp({...newBodyComp, right_thigh: parseFloat(e.target.value)})}
                    className="bg-secondary"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-gray-400 text-xs">Notes</label>
                <Textarea
                  value={newBodyComp.notes || ''}
                  onChange={(e) => setNewBodyComp({...newBodyComp, notes: e.target.value})}
                  className="bg-secondary"
                  rows={2}
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleAddBodyComp} disabled={saving} className="bg-primary">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Entry
                </Button>
                <Button variant="outline" onClick={() => setShowAddBodyComp(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Progress Chart */}
          {chartData.length > 0 && (
            <div className="card-tactical">
              <h3 className="font-heading text-lg text-white mb-4">Progress Chart</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#666" fontSize={12} />
                    <YAxis stroke="#666" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="weight" stroke="#ef4444" name="Weight (lbs)" strokeWidth={2} dot={{ fill: '#ef4444' }} />
                    <Line type="monotone" dataKey="body_fat" stroke="#8b5cf6" name="Body Fat %" strokeWidth={2} dot={{ fill: '#8b5cf6' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* History Table with Photos */}
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white mb-4">History</h3>
            {body_compositions.length > 0 ? (
              <div className="space-y-4">
                {body_compositions.map((entry, idx) => (
                  <div key={entry.id || idx} className="border border-border rounded-lg p-4">
                    <div className="flex flex-wrap justify-between items-start mb-3">
                      <div>
                        <span className="text-white font-medium">{entry.date}</span>
                        <div className="flex gap-4 mt-1 text-sm">
                          <span className="text-gray-400">Weight: <span className="text-white">{entry.weight_lbs || '-'} lbs</span></span>
                          <span className="text-gray-400">BF: <span className="text-white">{entry.body_fat_percentage || '-'}%</span></span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                        {entry.chest && <span>Chest: {entry.chest}"</span>}
                        {entry.waist && <span>Waist: {entry.waist}"</span>}
                        {entry.hips && <span>Hips: {entry.hips}"</span>}
                      </div>
                    </div>
                    
                    {/* Photos for this entry */}
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      {['front', 'side', 'back'].map((type) => {
                        const photoField = `${type}_photo`;
                        const hasPhoto = entry[photoField];
                        const isUploading = uploadingPhoto === `${entry.id}-${type}`;
                        
                        return (
                          <div key={type} className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              ref={el => {
                                if (!photoInputRefs[type]) photoInputRefs[type] = {};
                                photoInputRefs[type][entry.id] = el;
                              }}
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handlePhotoUpload(entry.id, type, e.target.files[0]);
                                }
                              }}
                            />
                            
                            {hasPhoto ? (
                              <div 
                                className="aspect-[3/4] bg-secondary rounded-md overflow-hidden relative group cursor-pointer"
                                onClick={() => setShowPhotoModal({ photo: entry[photoField], type, date: entry.date, entry })}
                              >
                                <img 
                                  src={entry[photoField]} 
                                  alt={`${type} view`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                  <button className="p-2 bg-white/20 rounded-full hover:bg-white/30">
                                    <ZoomIn className="w-4 h-4 text-white" />
                                  </button>
                                  <button 
                                    className="p-2 bg-red-500/50 rounded-full hover:bg-red-500/70"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePhoto(entry.id, type);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 text-white" />
                                  </button>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-center py-1">
                                  <span className="text-xs text-white capitalize">{type}</span>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  const ref = photoInputRefs[type]?.[entry.id];
                                  if (ref) ref.click();
                                }}
                                disabled={isUploading}
                                className="aspect-[3/4] w-full bg-secondary/50 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center hover:border-primary transition-colors"
                              >
                                {isUploading ? (
                                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                ) : (
                                  <>
                                    <Camera className="w-6 h-6 text-gray-500 mb-1" />
                                    <span className="text-xs text-gray-500 capitalize">{type}</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {entry.notes && (
                      <p className="text-gray-400 text-sm mt-3 italic">{entry.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">No body composition entries yet.</p>
            )}
          </div>

          {/* Progress Photos Gallery */}
          {progressPhotos.length > 0 && (
            <div className="card-tactical">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg text-white">Progress Photos</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowCompareMode(!showCompareMode)}
                  className={showCompareMode ? 'bg-primary/20 border-primary' : ''}
                >
                  <ArrowLeftRight className="w-4 h-4 mr-2" />
                  {showCompareMode ? 'Exit Compare' : 'Compare'}
                </Button>
              </div>
              
              {showCompareMode ? (
                <div className="space-y-4">
                  <p className="text-gray-400 text-sm">Select a "Before" and "After" photo to compare side by side.</p>
                  
                  {/* Comparison View */}
                  {comparePhotos.before && comparePhotos.after && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-center text-sm text-gray-400 mb-2">
                          Before: {comparePhotos.before.date}
                          {comparePhotos.before.weight_lbs && ` • ${comparePhotos.before.weight_lbs} lbs`}
                        </div>
                        <img 
                          src={comparePhotos.before.photo} 
                          alt="Before" 
                          className="w-full rounded-md"
                        />
                      </div>
                      <div>
                        <div className="text-center text-sm text-gray-400 mb-2">
                          After: {comparePhotos.after.date}
                          {comparePhotos.after.weight_lbs && ` • ${comparePhotos.after.weight_lbs} lbs`}
                        </div>
                        <img 
                          src={comparePhotos.after.photo} 
                          alt="After" 
                          className="w-full rounded-md"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Photo Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-300 text-sm mb-2 block">Select Before Photo</label>
                      <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                        {progressPhotos.flatMap(entry => 
                          ['front_photo', 'side_photo', 'back_photo']
                            .filter(key => entry[key])
                            .map(key => ({
                              id: `${entry.id}-${key}`,
                              photo: entry[key],
                              date: entry.date,
                              weight_lbs: entry.weight_lbs,
                              type: key.replace('_photo', '')
                            }))
                        ).map((item) => (
                          <button
                            key={item.id}
                            onClick={() => setComparePhotos(prev => ({ ...prev, before: item }))}
                            className={`aspect-square rounded-md overflow-hidden border-2 transition-all ${
                              comparePhotos.before?.id === item.id ? 'border-primary' : 'border-transparent'
                            }`}
                          >
                            <img src={item.photo} alt={item.date} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm mb-2 block">Select After Photo</label>
                      <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                        {progressPhotos.flatMap(entry => 
                          ['front_photo', 'side_photo', 'back_photo']
                            .filter(key => entry[key])
                            .map(key => ({
                              id: `${entry.id}-${key}`,
                              photo: entry[key],
                              date: entry.date,
                              weight_lbs: entry.weight_lbs,
                              type: key.replace('_photo', '')
                            }))
                        ).map((item) => (
                          <button
                            key={item.id}
                            onClick={() => setComparePhotos(prev => ({ ...prev, after: item }))}
                            className={`aspect-square rounded-md overflow-hidden border-2 transition-all ${
                              comparePhotos.after?.id === item.id ? 'border-green-500' : 'border-transparent'
                            }`}
                          >
                            <img src={item.photo} alt={item.date} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {progressPhotos.slice(0, 8).map((entry) => (
                    <div key={entry.id} className="space-y-2">
                      <div className="text-xs text-gray-400 text-center">{entry.date}</div>
                      <div className="grid grid-cols-3 gap-1">
                        {entry.front_photo && (
                          <button
                            onClick={() => setShowPhotoModal({ photo: entry.front_photo, type: 'front', date: entry.date, entry })}
                            className="aspect-square rounded overflow-hidden"
                          >
                            <img src={entry.front_photo} alt="Front" className="w-full h-full object-cover hover:opacity-80 transition" />
                          </button>
                        )}
                        {entry.side_photo && (
                          <button
                            onClick={() => setShowPhotoModal({ photo: entry.side_photo, type: 'side', date: entry.date, entry })}
                            className="aspect-square rounded overflow-hidden"
                          >
                            <img src={entry.side_photo} alt="Side" className="w-full h-full object-cover hover:opacity-80 transition" />
                          </button>
                        )}
                        {entry.back_photo && (
                          <button
                            onClick={() => setShowPhotoModal({ photo: entry.back_photo, type: 'back', date: entry.date, entry })}
                            className="aspect-square rounded overflow-hidden"
                          >
                            <img src={entry.back_photo} alt="Back" className="w-full h-full object-cover hover:opacity-80 transition" />
                          </button>
                        )}
                      </div>
                      {entry.weight_lbs && (
                        <div className="text-xs text-center text-gray-500">{entry.weight_lbs} lbs</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Photo Modal */}
      {showPhotoModal && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPhotoModal(null)}
        >
          <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <span className="text-white font-medium capitalize">{showPhotoModal.type} View</span>
                <span className="text-gray-400 ml-3">{showPhotoModal.date}</span>
                {showPhotoModal.entry?.weight_lbs && (
                  <span className="text-gray-400 ml-3">{showPhotoModal.entry.weight_lbs} lbs</span>
                )}
              </div>
              <button onClick={() => setShowPhotoModal(null)} className="text-white hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>
            <img 
              src={showPhotoModal.photo} 
              alt={showPhotoModal.type}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* AI Summaries Tab */}
      {activeTab === 'summaries' && (
        <div className="space-y-6">
          {/* Generate Summary Card */}
          <div className="card-tactical">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg text-white uppercase">Generate AI Summary</h3>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-gray-400 text-xs block mb-1">Summary Type</label>
                <select
                  value={summaryType}
                  onChange={(e) => setSummaryType(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                  data-testid="summary-type-select"
                >
                  <option value="weekly">Weekly Progress</option>
                  <option value="monthly">Monthly Report</option>
                  <option value="transformation">Transformation Story</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-gray-400 text-xs block mb-1">Coach's Note (Optional)</label>
                <input
                  type="text"
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                  placeholder="Add context for a more personalized summary..."
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                />
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <Button 
                onClick={generateSummary} 
                disabled={generatingSummary}
                className="bg-gradient-to-r from-primary to-purple-600"
                data-testid="generate-summary-btn"
              >
                {generatingSummary ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate {summaryType === 'weekly' ? 'Weekly' : summaryType === 'monthly' ? 'Monthly' : 'Transformation'} Summary
                  </>
                )}
              </Button>
              
              <div className="text-xs text-gray-500">
                Uses AI to analyze progress data and create personalized summaries
              </div>
            </div>
            
            {/* Email Service Status */}
            {emailServiceStatus && (
              <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md ${
                emailServiceStatus.configured 
                  ? 'bg-green-500/10 text-green-400' 
                  : 'bg-yellow-500/10 text-yellow-400'
              }`}>
                {emailServiceStatus.configured ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Email service ready - summaries can be sent directly to clients</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    <span>{emailServiceStatus.message}</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Latest Generated Summary */}
          {latestSummary && (
            <div className="card-tactical border-2 border-primary/30 bg-primary/5">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <FileBarChart className="w-5 h-5 text-primary" />
                  <span className="text-primary font-medium">Just Generated</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(latestSummary.summary)}
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => sendProgressEmail(latestSummary.summary_id)}
                    disabled={sendingEmail || !emailServiceStatus?.configured}
                    title={emailServiceStatus?.configured ? "Send as email" : "Email not configured"}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              {/* Progress Data */}
              {latestSummary.progress_data && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-secondary/50 rounded-md">
                  {latestSummary.progress_data.weight_change !== undefined && (
                    <div>
                      <div className="text-xs text-gray-400">Weight Change</div>
                      <div className={`font-mono ${latestSummary.progress_data.weight_change < 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {latestSummary.progress_data.weight_change > 0 ? '+' : ''}{latestSummary.progress_data.weight_change} lbs
                      </div>
                    </div>
                  )}
                  {latestSummary.progress_data.body_fat_change !== undefined && (
                    <div>
                      <div className="text-xs text-gray-400">Body Fat Change</div>
                      <div className={`font-mono ${latestSummary.progress_data.body_fat_change < 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {latestSummary.progress_data.body_fat_change > 0 ? '+' : ''}{latestSummary.progress_data.body_fat_change}%
                      </div>
                    </div>
                  )}
                  {latestSummary.progress_data.waist_change !== undefined && (
                    <div>
                      <div className="text-xs text-gray-400">Waist Change</div>
                      <div className={`font-mono ${latestSummary.progress_data.waist_change < 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {latestSummary.progress_data.waist_change > 0 ? '+' : ''}{latestSummary.progress_data.waist_change}"
                      </div>
                    </div>
                  )}
                  {latestSummary.progress_data.entries_count !== undefined && (
                    <div>
                      <div className="text-xs text-gray-400">Entries Tracked</div>
                      <div className="font-mono text-white">{latestSummary.progress_data.entries_count}</div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="prose prose-invert max-w-none">
                <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {latestSummary.summary}
                </div>
              </div>
            </div>
          )}

          {/* Previous Summaries */}
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Previous Summaries
            </h3>
            
            {summaries.length > 0 ? (
              <div className="space-y-4">
                {summaries.map((summary) => (
                  <div key={summary.id} className="border border-border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          summary.summary_type === 'weekly' ? 'bg-blue-500/20 text-blue-400' :
                          summary.summary_type === 'monthly' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {summary.summary_type === 'weekly' ? 'Weekly' : 
                           summary.summary_type === 'monthly' ? 'Monthly' : 'Transformation'}
                        </span>
                        <span className="text-gray-500 text-xs ml-2">
                          {new Date(summary.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(summary.summary_text)}
                          title="Copy"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {emailServiceStatus?.configured && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => sendProgressEmail(summary.id)}
                            disabled={sendingEmail}
                            className="text-blue-400 hover:text-blue-300"
                            title="Send as email"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteSummary(summary.id)}
                          className="text-red-400 hover:text-red-300"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="text-gray-300 text-sm whitespace-pre-wrap line-clamp-6">
                      {summary.summary_text}
                    </div>
                    
                    {summary.custom_note && (
                      <div className="mt-2 text-xs text-gray-500 italic">
                        Coach's note: {summary.custom_note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No summaries generated yet.</p>
                <p className="text-gray-500 text-sm">Generate your first AI-powered progress summary above!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="space-y-4">
          {/* Add Note Button */}
          <div className="flex justify-end">
            <Button onClick={() => setShowAddNote(!showAddNote)} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </div>

          {/* Add Note Form */}
          {showAddNote && (
            <div className="card-tactical">
              <h3 className="font-heading text-lg text-white mb-4">New Note</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-xs">Category</label>
                  <select
                    value={newNote.category}
                    onChange={(e) => setNewNote({...newNote, category: e.target.value})}
                    className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-white"
                  >
                    <option value="general">General</option>
                    <option value="workout">Workout</option>
                    <option value="nutrition">Nutrition</option>
                    <option value="progress">Progress</option>
                    <option value="concern">Concern</option>
                    <option value="goal">Goal</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Note</label>
                  <Textarea
                    value={newNote.note}
                    onChange={(e) => setNewNote({...newNote, note: e.target.value})}
                    className="bg-secondary"
                    rows={4}
                    placeholder="Write your note here..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddNote} disabled={saving} className="bg-primary">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Note
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddNote(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Notes List */}
          {notes.length > 0 ? (
            <div className="space-y-3">
              {notes.map((note) => (
                <div key={note.id} className="card-tactical">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${getCategoryColor(note.category)}`}>
                        {note.category}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {new Date(note.created_at).toLocaleDateString()} by {note.trainer_name}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="text-gray-500 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-white mt-2">{note.note}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-tactical text-center py-8">
              <StickyNote className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No notes yet. Add your first note for this client.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'programs' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => navigate('/programs')} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Assign Program
            </Button>
          </div>

          {programs.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {programs.map((prog) => (
                <div key={prog.id} className="card-tactical">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-semibold">{prog.program_name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      prog.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {prog.status}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    Assigned: {new Date(prog.assigned_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-tactical text-center py-8">
              <Dumbbell className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No programs assigned yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
